import { Alert, Box, Button, CircularProgress, Grid, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { useIntegrationsStatusContext } from '../../../context/IntegrationsStatusContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { Platform, PlatformImporter } from '../../../services/integration.types';
import { Integration } from '../../../services/mocks/data/integrations';
import { QUERY_KEYS } from '../../../utils/queryKeys';
import { useIntegrationMutations } from '../hooks/useIntegrationMutations';
import ExchangeApiPreview from './ExchangeApiPreview';
import {
    BitgetForm,
    BitpandaForm,
    DefaultExchangeForm,
    isBitgetFormValid,
    isBitpandaFormValid,
    isDefaultFormValid
} from './exchange-api-forms';


interface IntegrationExchangeApiFormComponent {
    platform: Platform;
    importer: PlatformImporter;
    onJobCreated: () => void;
}

export interface ExchangeApiFormProps {
    formData: {
        key_post: string;
        secret_post: string;
        passphrase_post?: string;
    };
    setFormData: (data: any) => void;
}

const IntegrationExchangeApiFormComponent: React.FC<IntegrationExchangeApiFormComponent> = ({
    platform,
    importer,
    onJobCreated,
}) => {
    const { t } = useTranslation();
    const { integrations } = useIntegrationsStatusContext();
    const { showNotification } = useNotification();
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [formData, setFormData] = useState({
        key_post: '',
        secret_post: '',
        passphrase_post: '',
    });
    const [showJobWarning, setShowJobWarning] = useState(false);
    const [showEditMode, setShowEditMode] = useState(false);
    const [isQueryInvalidating, setIsQueryInvalidating] = useState(false);

    const { createExchangeIntegration, isExchangeCreating, deleteIntegration } = useIntegrationMutations();

    // Combined loading state to check if any operation is in progress
    const isLoading = isExchangeCreating || isQueryInvalidating;

    // Find existing integration for this platform
    const existingIntegration = useMemo(() => {
        if (!integrations || !platform) return null;
        return integrations.find((integration) => integration.platformName === platform.name && integration.jobId);
    }, [integrations, platform]);

    // Determine whether to show the form or preview
    const showForm = useMemo(() => {
        // When handleEdit is called, we show the form even with an existing integration
        return !existingIntegration || existingIntegration.jobId === null || showEditMode;
    }, [existingIntegration, showEditMode]);

    const validateImporter = (importer: PlatformImporter) => {
        return !importer.uri ? ['uri'] : [];
    };

    const missingFields = validateImporter(importer);
    const isImporterValid = missingFields.length === 0;

    const handleSubmit = async () => {
        setShowJobWarning(false);

        createExchangeIntegration(
            { importerUri: importer.uri, data: formData },
            {
                onSuccess: async () => {
                    setIsQueryInvalidating(true);
                    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
                    showNotification(
                        t('common.integration.syncStarted', { platform: platform.display_name }),
                        'success'
                    );
                    setIsQueryInvalidating(false);
                    // Wait briefly to let user see the success message before navigating away
                    setTimeout(() => {
                        onJobCreated();
                    }, 1500);
                },
                onError: (error) => {
                    // Backend normalizes responses, so errors here are real failures
                    console.error('Exchange integration error:', error);
                    const errorMessage = error?.response?.data?.error ||
                                       error?.response?.data?.message ||
                                       error?.message ||
                                       t('integrations.exchange.api.credentialsWarning');
                    showNotification(errorMessage, 'error');
                    setShowJobWarning(true);
                    // Don't call onJobCreated() - integration was not created
                }
            }
        );
    };

    const handleEdit = () => {
        setShowEditMode(true);
        setShowJobWarning(false);
    };

    // Helper function to check if the form is valid based on the platform
    const isFormValid = () => {
        switch (platform.name) {
            case 'bitpanda':
                return isBitpandaFormValid(formData);
            case 'bitget':
                return isBitgetFormValid(formData);
            default:
                return isDefaultFormValid(formData);
        }
    };

    // Helper function to render the appropriate form based on platform
    const renderExchangeForm = () => {
        switch (platform.name) {
            case 'bitpanda':
                return <BitpandaForm formData={formData} setFormData={setFormData} />;
            case 'bitget':
                return <BitgetForm formData={formData} setFormData={setFormData} />;
            default:
                return <DefaultExchangeForm formData={formData} setFormData={setFormData} />;
        }
    };

    if (!platform || !importer)
        return (
            <Typography variant="h6" gutterBottom>
                {t('integrations.exchange.selectIntegration')}
            </Typography>
        );

    return (
        <>
            {showForm ? (
                <Grid container spacing={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                mt: 2,
                            }}
                        >
                            {existingIntegration && (
                                <Alert severity="error">
                                    {t('integrations.exchange.api.replacementWarning')}
                                </Alert>
                            )}

                            {!isImporterValid && (
                                <Alert severity="warning">
                                    {t('integrations.exchange.api.missingFields', { fields: missingFields.join(', ') })}
                                </Alert>
                            )}

                            <Alert severity="warning" sx={{ mb: 2 }}>
                                {t('integrations.exchange.api.securityWarning')}
                            </Alert>

                            {renderExchangeForm()}

                            <Button
                                variant="contained"
                                fullWidth
                                onClick={handleSubmit}
                                disabled={!isFormValid() || isLoading || !isImporterValid}
                                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
                            >
                                {isLoading ? t('common.integration.connecting') : t('common.integration.connect')}
                            </Button>                            

                            {showJobWarning && (
                                <Alert severity="warning">{t('integrations.exchange.api.credentialsWarning')}</Alert>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                p: 2,
                                height: '100%',
                                bgcolor:
                                    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                                borderRadius: 1,
                            }}
                        >
                            {importer.customText ? (
                                <Typography
                                    variant="body2"
                                    dangerouslySetInnerHTML={{ __html: importer.customText }}
                                    sx={{
                                        pb: 1,
                                        '& *': {
                                            color:
                                                theme.palette.mode === 'dark'
                                                    ? theme.palette.text.primary + ' !important'
                                                    : 'inherit',
                                        },
                                    }}
                                />
                            ) : (
                                <>
                                    <Typography variant="body2">
                                        Connect your {platform.display_name} account using API:
                                    </Typography>
                                    <Box
                                        component="ul"
                                        sx={{
                                            pl: 2,
                                            m: 0,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography component="li" variant="body2">
                                            Get read-only API access
                                        </Typography>
                                        <Typography component="li" variant="body2">
                                            Sync transactions automatically
                                        </Typography>
                                    </Box>
                                </>
                            )}
                        </Box>
                    </Grid>
                </Grid>
            ) : (
                <ExchangeApiPreview
                    integration={existingIntegration as Integration}
                    platform={platform}
                    onEdit={handleEdit}
                />
            )}
        </>
    );
};

export default IntegrationExchangeApiFormComponent;
